import ConditionMatcher from "./ConditionMatcher"
import EvaluateRequest from "../EvaluateRequest"
import { EvaluatorContext } from "../evaluator/Evaluator"
import { DecisionReason, ExperimentConfig, TargetCondition } from "../../model/model"
import ValueOperatorMatcher from "./ValueOperatorMatcher"
import ExperimentEvaluation from "../service/experiment/ExperimentEvaluation"
import ObjectUtil from "../../util/ObjectUtil"
import TypeUtil from "../../util/TypeUtil"
import ExperimentReferenceLocalEvaluator from "../service/experiment/mode/local/ExperimentReferenceLocalEvaluator"
import LocalEvaluateRequest, { isLocalEvaluateRequest } from "../mode/local/LocalEvaluateRequest"

export default class ExperimentConditionMatcher implements ConditionMatcher {
  private readonly abTestMatcher: AbTestReferenceLocalEvaluateMatcher
  private readonly featureFlagMatcher: FeatureFlagReferenceLocalEvaluateMatcher

  constructor(
    abTestMatcher: AbTestReferenceLocalEvaluateMatcher,
    featureFlagMatcher: FeatureFlagReferenceLocalEvaluateMatcher
  ) {
    this.abTestMatcher = abTestMatcher
    this.featureFlagMatcher = featureFlagMatcher
  }

  /**
   * @override
   */
  matches(request: EvaluateRequest, context: EvaluatorContext, condition: TargetCondition): boolean {
    // Reference matching runs only during local evaluation against the on-device config; remote
    // evaluation resolves targeting on the server and never reaches this matcher.
    if (!isLocalEvaluateRequest(request)) {
      return false
    }
    switch (condition.key.type) {
      case "AB_TEST":
        return this.abTestMatcher.matches(request, context, condition)
      case "FEATURE_FLAG":
        return this.featureFlagMatcher.matches(request, context, condition)
      default:
        throw new Error(`Unsupported TargetKeyType [${condition.key.type}]`)
    }
  }
}

abstract class ExperimentReferenceMatcher {
  protected constructor(
    protected readonly experimentReferenceLocalEvaluator: ExperimentReferenceLocalEvaluator,
    protected readonly valueOperatorMatcher: ValueOperatorMatcher
  ) {}

  matches(request: LocalEvaluateRequest, context: EvaluatorContext, condition: TargetCondition): boolean {
    const key = ObjectUtil.requiredNotNullOrUndefined(
      TypeUtil.asNumber(condition.key.name),
      () => `Invalid key [${condition.key.type}, ${condition.key.name}]`
    )

    const experiment = this.experiment(request, key)
    if (ObjectUtil.isNullOrUndefined(experiment)) {
      return false
    }

    const evaluation = this.experimentReferenceLocalEvaluator.evaluate(request, context, experiment)
    return this.evaluationMatches(evaluation, condition)
  }

  abstract experiment(request: LocalEvaluateRequest, key: number): ExperimentConfig | undefined

  abstract evaluationMatches(evaluation: ExperimentEvaluation, condition: TargetCondition): boolean
}

export class AbTestReferenceLocalEvaluateMatcher extends ExperimentReferenceMatcher {
  constructor(
    experimentReferenceLocalEvaluator: ExperimentReferenceLocalEvaluator,
    valueOperatorMatcher: ValueOperatorMatcher
  ) {
    super(experimentReferenceLocalEvaluator, valueOperatorMatcher)
  }

  private static AB_TEST_MATCHED_REASONS: DecisionReason[] = [
    DecisionReason.OVERRIDDEN,
    DecisionReason.TRAFFIC_ALLOCATED,
    DecisionReason.EXPERIMENT_COMPLETED
  ]

  /**
   * @override
   */
  experiment(request: LocalEvaluateRequest, key: number): ExperimentConfig | undefined {
    return request.workspace.getExperimentOrNull(key)
  }

  /**
   * @override
   */
  evaluationMatches(evaluation: ExperimentEvaluation, condition: TargetCondition): boolean {
    if (!AbTestReferenceLocalEvaluateMatcher.AB_TEST_MATCHED_REASONS.includes(evaluation.result.reason)) {
      return false
    }
    return this.valueOperatorMatcher.matches(evaluation.result.variation.key, condition.match)
  }
}

export class FeatureFlagReferenceLocalEvaluateMatcher extends ExperimentReferenceMatcher {
  constructor(
    experimentReferenceLocalEvaluator: ExperimentReferenceLocalEvaluator,
    valueOperatorMatcher: ValueOperatorMatcher
  ) {
    super(experimentReferenceLocalEvaluator, valueOperatorMatcher)
  }

  /**
   * @override
   */
  experiment(request: LocalEvaluateRequest, key: number): ExperimentConfig | undefined {
    return request.workspace.getFeatureFlagOrNull(key)
  }

  /**
   * @override
   */
  evaluationMatches(evaluation: ExperimentEvaluation, condition: TargetCondition): boolean {
    const on = evaluation.result.variation.key !== "A"
    return this.valueOperatorMatcher.matches(on, condition.match)
  }
}
